import type { CurrentUser, Vendor } from '@arbuz/shared';
import { makeT } from '../i18n';
import { haptic } from '../tg';
import { ThemedIcon, type ThemedIconName } from './clientUi';
import { EmptyState } from './vendorUi';

interface Props {
  user: CurrentUser;
  vendor: Vendor | null;
  vendorError: string | null;
  onPayout: () => void;
  onSchedule: () => void;
  onPickup: () => void;
  onRetryVendor: () => void;
}

function maskCard(card: string | null): string {
  if (!card) return '—';
  return `${card.slice(0, 4)} ${card.slice(4, 6)}•• •••• ${card.slice(-4)}`;
}

function Row({
  icon,
  label,
  value,
  onClick,
}: {
  icon: ThemedIconName;
  label: string;
  value: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="card courier-section-row"
      onClick={() => {
        haptic('light');
        onClick();
      }}
    >
      <span className="courier-icon-circle" aria-hidden="true">
        <ThemedIcon name={icon} />
      </span>
      <span>
        <span className="muted" style={{ display: 'block', fontSize: 'var(--fs-sm)' }}>
          {label}
        </span>
        <strong>{value}</strong>
      </span>
      <span className="courier-arrow" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

export function CourierProfileHub({
  user,
  vendor,
  vendorError,
  onPayout,
  onSchedule,
  onPickup,
  onRetryVendor,
}: Props) {
  const t = makeT(user.locale);

  return (
    <main className="page courier-page">
      <div className="courier-topline">
        <div>
          <p className="courier-eyebrow">{t('executorProfile')}</p>
          <h1 className="courier-title">{t('profileSettings')}</h1>
        </div>
        <span className="courier-pill">{user.locale.toUpperCase()}</span>
      </div>

      {vendorError ? (
        <EmptyState
          tone="error"
          title={t('shopLoadError')}
          text={vendorError}
          actionLabel={t('retry')}
          onAction={onRetryVendor}
        />
      ) : vendor ? (
        <div className="courier-row-list">
          <Row icon="orders" label={t('shopNameLabel')} value={vendor.display_name} onClick={onPayout} />
          <Row
            icon="phone"
            label={t('payoutPhoneLabel')}
            value={vendor.contact_phone ?? '—'}
            onClick={onPayout}
          />
          <Row
            icon="card"
            label={t('payoutCardLabel')}
            value={maskCard(vendor.payout_card_number)}
            onClick={onPayout}
          />
          <Row
            icon="clock"
            label={t('scheduleTitle')}
            value={`${vendor.open_days_count}/7`}
            onClick={onSchedule}
          />
          <Row
            icon="delivery"
            label={t('shopCapacityLabel')}
            value={`${vendor.capacity_per_slot} ${t('deliveries')}`}
            onClick={onPayout}
          />
          <Row
            icon="location"
            label={t('pickupTitle')}
            value={user.pickup_address_text ?? t('pickupNotSet')}
            onClick={onPickup}
          />
        </div>
      ) : (
        <EmptyState title={t('shopNone')} text={t('shopNoneHint')} />
      )}
    </main>
  );
}
